import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { X } from 'lucide-react';

/**
 * Text input that filters a local options list as you type.
 * Selecting an option commits it; clearing empties the value (parent can treat as "any").
 */
const LocalTypeahead = ({
  options = [],
  value = '',
  onChange,
  placeholder = 'Any',
  ariaLabel = 'Filter',
  emptyMessage = 'No matches',
}) => {
  const reactId = useId();
  const listboxId = `${reactId}-listbox`;
  const optionId = (index) => `${reactId}-option-${index}`;
  const [query, setQuery] = useState(value || '');
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const wrapRef = useRef(null);
  const listRef = useRef(null);

  useEffect(() => {
    setQuery(value || '');
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target)) {
        setOpen(false);
        setActiveIndex(-1);
        setQuery(value || '');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [value]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((opt) => String(opt).toLowerCase().startsWith(q));
  }, [options, query]);

  useEffect(() => {
    if (activeIndex < 0 || !listRef.current) return;
    const el = listRef.current.querySelector(`[data-option-index="${activeIndex}"]`);
    if (el) el.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  const handlePick = (opt) => {
    onChange(opt);
    setQuery(opt);
    setOpen(false);
    setActiveIndex(-1);
  };

  const clearInput = () => {
    setQuery('');
    onChange('');
    setOpen(false);
    setActiveIndex(-1);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      if (open) {
        e.preventDefault();
        setOpen(false);
        setActiveIndex(-1);
        setQuery(value || '');
      }
      return;
    }

    if (e.key !== 'Enter' && e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    if (!open || filtered.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((prev) => (prev < filtered.length - 1 ? prev + 1 : 0));
      return;
    }

    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((prev) => (prev > 0 ? prev - 1 : filtered.length - 1));
      return;
    }

    e.preventDefault();
    e.stopPropagation();
    const index = activeIndex >= 0 && activeIndex < filtered.length ? activeIndex : 0;
    handlePick(filtered[index]);
  };

  const showClear = Boolean(query);
  const activeOptionId = activeIndex >= 0 ? optionId(activeIndex) : undefined;

  return (
    <div className="criteria-ta-wrap" ref={wrapRef}>
      <div className="criteria-search-field">
        <input
          type="text"
          className={`criteria-search-input${showClear ? ' has-clear' : ''}`}
          placeholder={placeholder}
          value={query}
          autoComplete="off"
          role="combobox"
          aria-label={ariaLabel}
          aria-expanded={open}
          aria-controls={listboxId}
          aria-autocomplete="list"
          aria-activedescendant={activeOptionId}
          onChange={(e) => {
            const next = e.target.value;
            setQuery(next);
            setOpen(true);
            setActiveIndex(-1);
            if (!next.trim() && value) {
              onChange('');
            }
          }}
          onFocus={() => {
            setOpen(true);
            setActiveIndex(filtered.length > 0 ? 0 : -1);
          }}
          onKeyDown={handleKeyDown}
        />
        {showClear && (
          <button
            type="button"
            className="criteria-search-clear"
            aria-label="Clear"
            onClick={clearInput}
          >
            <X className="h-4 w-4" aria-hidden />
          </button>
        )}
      </div>
      {open && (
        <ul id={listboxId} className="criteria-ta-results" role="listbox" ref={listRef}>
          {filtered.length === 0 ? (
            <li>
              <button type="button" disabled>
                {emptyMessage}
              </button>
            </li>
          ) : (
            filtered.map((opt, index) => (
              <li key={opt} role="presentation">
                <button
                  type="button"
                  id={optionId(index)}
                  role="option"
                  aria-selected={index === activeIndex}
                  data-option-index={index}
                  className={index === activeIndex ? 'is-active' : undefined}
                  onMouseEnter={() => setActiveIndex(index)}
                  onClick={() => handlePick(opt)}
                >
                  {opt}
                </button>
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
};

export default LocalTypeahead;
